import React, { Component } from 'react';
import { Box, Text, Flex, Heading, Card } from 'core/components';
import styled from 'styled-components';
import Table from 'core/components/HTMLTable';

import { observer } from 'mobx-react';
import { FiTarget, FiServer } from 'react-icons/fi';
import ToolResultCard from '../components/ToolResultCard';

const StatsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin: 8px 0;
`;

const StatItem = styled(Box)`
  text-align: center;

  .stat-label {
    font-size: 11px;
    color: ${({ theme }) => theme.colors.muted};
    text-transform: uppercase;
    font-weight: 600;
  }

  .stat-value {
    font-size: 16px;
    font-weight: bold;
  }
`;

const ResultSection = styled(Card)`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .section-header {
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .section-content {
    padding: 12px;
  }
`;

const HostnameDiv = styled.div`
  font-size: 11px;
  color: #666;
`;

@observer
class ConnectivityTestToolResult extends Component {
  formatRtt(rtt) {
    return `${rtt.toFixed(3)} ms`;
  }

  renderPingStatistics(statistics, pingSent, responses) {
    if (!statistics) {
      return null;
    }

    // Calculate actual packet loss from responses if available
    let packetLoss = statistics.loss;
    if (responses && responses.length > 0) {
      const receivedCount = responses.filter((r) => r.from && r.rtt > 0).length;
      packetLoss = ((responses.length - receivedCount) / responses.length) * 100;
    }

    // Determine packets sent count - prefer responses.length if available
    const packetsSent = (responses && responses.length) || pingSent || statistics.replies || 0;

    return (
      <StatsGrid>
        <StatItem>
          <div className="stat-label">Avg RTT</div>
          <div className="stat-value">{this.formatRtt(statistics.avg)}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Min RTT</div>
          <div className="stat-value">{this.formatRtt(statistics.min)}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Max RTT</div>
          <div className="stat-value">{this.formatRtt(statistics.max)}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Packet Loss</div>
          <div className="stat-value">{packetLoss.toFixed(0)}%</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Std Dev</div>
          <div className="stat-value">{statistics.stddev.toFixed(3)}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Packets Sent</div>
          <div className="stat-value">{packetsSent}</div>
        </StatItem>
      </StatsGrid>
    );
  }

  renderPingResponses(responses) {
    if (!responses || responses.length === 0) {
      return null;
    }

    // Find the highest and lowest RTT values
    const maxRtt = Math.max(...responses.map((r) => r.rtt));
    const minRtt = Math.min(...responses.map((r) => r.rtt));

    return (
      <Table interactive bordered>
        <thead>
          <tr>
            <th>Seq</th>
            <th>Status</th>
            <th>RTT</th>
            <th>From</th>
            <th>TTL</th>
            <th>Size</th>
          </tr>
        </thead>
        <tbody>
          {responses.map((response) => {
            const isHighestRtt = response.rtt === maxRtt;
            const isLowestRtt = response.rtt === minRtt && maxRtt !== minRtt;

            let className = '';
            if (isHighestRtt) {
              className = 'max';
            } else if (isLowestRtt) {
              className = 'min';
            }

            // Determine if packet was received (has valid RTT and from address)
            const isReceived = response.from && response.rtt > 0;

            return (
              <tr key={`response-${response.seq}`} className={className}>
                <td>{response.seq}</td>
                <td>{isReceived ? '✅' : '❌'}</td>
                <td>{isReceived ? this.formatRtt(response.rtt) : '-'}</td>
                <td>{response.from || '-'}</td>
                <td>{response.replyTtl || '-'}</td>
                <td>{response.replySize ? `${response.replySize}B` : '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  renderPingResult(pingData, target, agentId, agent_name, testId) {
    if (!pingData || !pingData.ping) {
      return null;
    }

    const { ping } = pingData;
    const { statistics, responses } = ping;

    // Determine success indicator based on packet loss
    let statusIcon = '';
    if (statistics) {
      if (statistics.loss === 0) {
        statusIcon = '✅';
      } else if (statistics.loss === 100) {
        statusIcon = '❌';
      } else {
        statusIcon = '⚠️';
      }
    }

    return (
      <ResultSection key={`${target}-${agentId}`}>
        <div className="section-header">
          <FiTarget />
          <Text fontWeight="medium">
            {statusIcon} Ping Results: {ping.src} → {target}
          </Text>
          <Box ml="auto" fontSize="12px" color="muted">
            Agent: {agent_name || agentId}
          </Box>
        </div>
        <Flex flexDirection="column" gap={2}>
          <Text color="muted" small>
            {ping.method} | {ping.pingSent} probes | {ping.timeout}s timeout | {ping.probeSize}B probe size | id:
            {testId}
          </Text>

          {statistics && this.renderPingStatistics(statistics, ping.pingSent, responses)}

          {responses && responses.length > 0 && this.renderPingResponses(responses)}
        </Flex>
      </ResultSection>
    );
  }

  renderTraceStatistics(trace) {
    if (!trace || !trace.hops || trace.hops.length === 0) {
      return null;
    }

    const rtts = trace.hops.map((hop) => hop.rtt);
    const avgRtt = rtts.length > 0 ? rtts.reduce((sum, rtt) => sum + rtt, 0) / rtts.length : 0;
    const minRtt = rtts.length > 0 ? Math.min(...rtts) : 0;
    const maxRtt = rtts.length > 0 ? Math.max(...rtts) : 0;

    // Calculate standard deviation
    const variance = rtts.length > 0 ? rtts.reduce((sum, rtt) => sum + (rtt - avgRtt) ** 2, 0) / rtts.length : 0;
    const stddev = Math.sqrt(variance);

    return (
      <StatsGrid>
        <StatItem>
          <div className="stat-label">Hop Count</div>
          <div className="stat-value">{trace.hopCount}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Avg RTT</div>
          <div className="stat-value">{this.formatRtt(avgRtt)}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Min RTT</div>
          <div className="stat-value">{this.formatRtt(minRtt)}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Max RTT</div>
          <div className="stat-value">{this.formatRtt(maxRtt)}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Std Dev</div>
          <div className="stat-value">{stddev.toFixed(3)}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Method</div>
          <div className="stat-value">{trace.method}</div>
        </StatItem>
      </StatsGrid>
    );
  }

  renderTraceHops(hops) {
    if (!hops || hops.length === 0) {
      return null;
    }

    // Find the highest and lowest RTT values
    const validRtts = hops.map((hop) => hop.rtt).filter((rtt) => rtt > 0);
    const maxRtt = validRtts.length > 0 ? Math.max(...validRtts) : 0;
    const minRtt = validRtts.length > 0 ? Math.min(...validRtts) : 0;

    return (
      <Table interactive bordered>
        <thead>
          <tr>
            <th>Hop</th>
            <th>Address</th>
            <th>RTT</th>
            <th>ASN</th>
            <th>Reply TTL</th>
            <th>ICMP Type/Code</th>
            <th>Reply Size</th>
          </tr>
        </thead>
        <tbody>
          {hops.map((hop) => {
            const isHighestRtt = hop.rtt === maxRtt && hop.rtt > 0;
            const isLowestRtt = hop.rtt === minRtt && hop.rtt > 0 && maxRtt !== minRtt;

            let className = '';
            if (isHighestRtt) {
              className = 'max';
            } else if (isLowestRtt) {
              className = 'min';
            }

            // Format ASN display
            let asnDisplay = '-';
            if (hop.asn) {
              if (hop.asn.id && hop.asn.name) {
                asnDisplay = (
                  <div>
                    <div>AS{hop.asn.id}</div>
                    <HostnameDiv>{hop.asn.name}</HostnameDiv>
                  </div>
                );
              } else if (hop.asn.id) {
                asnDisplay = `AS${hop.asn.id}`;
              }
            }

            return (
              <tr key={`hop-${hop.probeTtl}`} className={className}>
                <td>{hop.probeTtl}</td>
                <td>
                  <div>
                    {hop.addr}
                    {hop.name && <HostnameDiv>{hop.name}</HostnameDiv>}
                  </div>
                </td>
                <td>{this.formatRtt(hop.rtt)}</td>
                <td>{asnDisplay}</td>
                <td>{hop.replyTtl}</td>
                <td>
                  {hop.icmpType}/{hop.icmpCode}
                </td>
                <td>{hop.replySize}B</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  renderTraceResult(traceData, target, agentId, agent_name, testId) {
    if (!traceData || !traceData.trace) {
      return null;
    }

    const { trace } = traceData;

    // Determine success indicator based on whether last hop matches target
    let statusIcon = '';
    if (trace.hops && trace.hops.length > 0) {
      const lastHop = trace.hops[trace.hops.length - 1];
      // Check if the last hop's address matches the destination
      const reachedTarget = lastHop.addr === trace.dst || lastHop.addr === target;
      statusIcon = reachedTarget ? '✅' : '❌';
    }

    return (
      <ResultSection key={`${target}-${agentId}-trace`}>
        <div className="section-header">
          <FiServer />
          <span>
            {statusIcon} Traceroute Results: {trace.src} → {trace.dst}
          </span>
          <Box ml="auto" fontSize="12px" color="muted">
            Agent: {agent_name || agentId}
          </Box>
        </div>
        <Flex flexDirection="column" gap={2}>
          <Text color="muted" small>
            {trace.method} | {trace.probeCount} probes | {trace.wait}s timeout | {trace.probeSize}B probe size | Stop:{' '}
            {trace.stopReason} | id: {testId}
          </Text>

          <Box>
            <Heading level={6} mb={1}>
              Statistics
            </Heading>
            {this.renderTraceStatistics(trace)}
          </Box>

          {trace.hops && trace.hops.length > 0 && (
            <Box>
              <Heading level={6} mb={1}>
                Hop Details
              </Heading>
              {this.renderTraceHops(trace.hops)}
            </Box>
          )}
        </Flex>
      </ResultSection>
    );
  }

  renderResults(data) {
    if (!data) {
      return (
        <Box p={2}>
          <Text color="muted">No results available</Text>
        </Box>
      );
    }

    // Handle both direct data and results structure
    const results = data.results || [];
    const testId = data.id || '';
    if (!Array.isArray(results) || results.length === 0) {
      return (
        <Box p={2}>
          <Text color="muted">No test results available</Text>
        </Box>
      );
    }

    return (
      <Box>
        {results.map((agentResult, index) => {
          const { result, target, agentId, agent_name } = agentResult;

          if (!result || !result.results) {
            return (
              <Box key={`agent-${agentId || index}-${target || 'unknown'}`} mb={2}>
                <Text color="muted">No results for {target || 'target'}</Text>
              </Box>
            );
          }

          return (
            <Box key={`agent-${agentId}-${target}`} mb={3}>
              {result.results.map((testResult, testIndex) => {
                if (testResult.ping) {
                  return this.renderPingResult(testResult, target, agentId, agent_name, testId);
                }
                if (testResult.trace) {
                  return this.renderTraceResult(testResult, target, agentId, agent_name, testId);
                }
                return (
                  <ResultSection key={`unknown-${agentId}-${target}-${testResult.type || testIndex}`}>
                    <div className="section-header">
                      <span>Unknown Result Type</span>
                    </div>
                    <div className="section-content">
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                        {JSON.stringify(testResult, null, 2)}
                      </pre>
                    </div>
                  </ResultSection>
                );
              })}
            </Box>
          );
        })}
      </Box>
    );
  }

  renderTestResults(tool) {
    const { raw_result, processed_result } = tool;

    if (!raw_result && !processed_result) {
      return (
        <Box p={2}>
          <Text color="muted">No results available</Text>
        </Box>
      );
    }

    return <Box p={2}>{raw_result && this.renderResults(raw_result)}</Box>;
  }

  render() {
    const { tool, onCloseToolResult, isInlineMode = false } = this.props;

    return (
      <ToolResultCard
        tool={tool}
        onCloseToolResult={onCloseToolResult}
        isInlineMode={isInlineMode}
        className="tool-result-connectivity-test"
        title={tool.title || 'Connectivity Test Tool Result'}
        overflow="hidden"
        maxHeight={isInlineMode ? '600px' : undefined}
      >
        <Box flex={1} overflow="auto">
          {this.renderTestResults(tool)}
        </Box>
      </ToolResultCard>
    );
  }
}

export default ConnectivityTestToolResult;
